import { error400Handler, error422Handler, request } from "@/Api";
import VendorForm from "./VendorForm";
import { useEffect, useState } from "react";
import { Button, CircularProgress, Grid, Snackbar } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { parseJwt } from "@/auth/authUtils";
import { useDispatch } from "react-redux";
import { setToken } from "@/auth/authSlice";
import { setError } from "@/nav/navSlice";
import Confirmation from "@/public/Confirmation";

export default function InvoicePortalVendorPage() {
    const [searchParams, _] = useSearchParams();
    const [vendor, setVendor] = useState({});
    const [errors, setErrors] = useState({});
    const [showSaved, setShowSaved] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [tokenIsValid, setTokenIsValid] = useState(false);

    const token = searchParams.get("token");
    const tokenInfo = parseJwt(token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const hasInvalidToken = token && !tokenIsValid;

    const getVendor = () => {
        if (!tokenInfo) {
            return;
        }
        request
            .get(`/vendors/${tokenInfo.vendor_id}/wildcard`, {
                params: { token },
            })
            .then((response) => {
                setVendor(response.data);
                setTokenIsValid(true);
            })
            .catch((err) => {
                error400Handler(err, (parsedError) => {
                    dispatch(setError({ message: parsedError }));
                    setTokenIsValid(false);
                });
            });
    };

    const putVendor = () => {
        if (!tokenInfo) {
            return;
        }
        setIsWaiting(true);
        request
            .put(`/vendors/${tokenInfo.vendor_id}/wildcard`, vendor, {
                params: { token: token },
            })
            .then((response) => {
                setVendor({
                    ...response.data,
                    tax_id: vendor.tax_id,
                    account_number: vendor.account_number,
                });
                setShowSaved(true);
            })
            .catch((err) => {
                error422Handler(err, (parsedError) => {
                    dispatch(
                        setError({
                            message:
                                "Your information has errors, please check the form.",
                        }),
                    );
                    setErrors(parsedError);
                });
                error400Handler(err, (parsedError) => {
                    dispatch(setError({ message: parsedError }));
                });
            })
            .finally(() => {
                setIsWaiting(false);
            });
    };

    const postVendor = () => {
        if (tokenInfo) {
            return;
        }
        setIsWaiting(true);
        request
            .post(`/vendors/wildcard`, vendor, {
                params: { schema: "organization_1" },
            })
            .then((response) => {
                setVendor(response.data);
                setShowSaved(true);
                navigate("/ap/vendor-thank-you");
            })
            .catch((err) => {
                error422Handler(err, (parsedError) => {
                    setErrors(parsedError);
                    dispatch(
                        setError({
                            message:
                                "Your information has errors, please check the form.",
                        }),
                    );
                });
                error400Handler(err, (parsedError) => {
                    dispatch(setError({ message: parsedError }));
                });
            })
            .finally(() => {
                setIsWaiting(false);
            });
    };

    useEffect(() => {
        getVendor();
        if (token && tokenInfo) {
            dispatch(setToken(token));
        }
    }, []);

    return (
        <>
            {!hasInvalidToken ? (
                <Grid container justifyContent={"center"} paddingTop={10}>
                    <Grid item width={"60%"} minWidth={800} paddingBottom={10}>
                        <VendorForm
                            isWildcard
                            vendor={vendor}
                            setVendor={setVendor}
                            showBankingInfo
                            errors={errors}
                            setErrors={setErrors}
                        />
                        <div style={{ textAlign: "right" }}>
                            {isWaiting ? (
                                <CircularProgress />
                            ) : (
                                <Button
                                    onClick={tokenInfo ? putVendor : postVendor}
                                    variant="contained"
                                    sx={{ marginTop: 5 }}
                                >
                                    Save
                                </Button>
                            )}
                        </div>
                    </Grid>
                </Grid>
            ) : (
                <Confirmation
                    description={
                        "Your vendor invitation is invalid or expired, but you can ask your producer for another invite!"
                    }
                />
            )}
            <Snackbar
                open={showSaved}
                autoHideDuration={2000}
                onClose={() => {
                    setShowSaved(false);
                }}
                message="Information saved"
            />
        </>
    );
}
